/**
 * ProgrammeNav — the left season-programme navigator (§5f).
 *
 * Lists the ModellingSession's month×day-type slices, each with a status dot and
 * its per-slice contribution, and the honest season roll-up in the footer: only
 * DESIGNED slices count, and partial coverage is labelled rather than presented
 * as the whole season (MASTER_PLAN §6.3). Presentational only.
 */
import type { SliceStatus } from './types'

export interface ProgrammeSliceRow {
  id: string
  title: string
  statusLabel: string
  status: SliceStatus
  /** Pre-formatted per-slice contribution, or null when not scored. */
  contrib: string | null
  active: boolean
}

export interface ProgrammeNavProps {
  seasonTag: string
  slices: ProgrammeSliceRow[]
  onSelect: (id: string) => void
  onAddSlice: () => void
  /** Button label — reflects whether the current design is new or an update. */
  addLabel: string
  /** Disabled until a timetable has actually been designed. */
  addDisabled: boolean
  rollup: { value: string; note: string; partial: boolean }
}

export function ProgrammeNav({ seasonTag, slices, onSelect, onAddSlice, addLabel, addDisabled, rollup }: ProgrammeNavProps) {
  return (
    <>
      <div className="sh-nav-head">
        <div className="sh-nav-head-row">
          <span className="sh-nav-title">Season programme</span>
          <span className="sh-nav-tag">{seasonTag}</span>
        </div>
        <p className="sh-nav-blurb">Each slice is a month × day-type with its own design, rolling up to the season case.</p>
      </div>

      <div className="sh-nav-list">
        {slices.map((s) => (
          <button
            key={s.id} type="button"
            className={`sh-slice${s.active ? ' sh-slice--active' : ''}`}
            onClick={() => onSelect(s.id)}
          >
            <div className="sh-slice-top">
              <span className="sh-slice-title">{s.title}</span>
              <span className={`sh-slice-dot sh-slice-dot--${s.status}`} />
            </div>
            <div className="sh-slice-meta">
              <span className="sh-slice-status">{s.statusLabel}</span>
              <span className={`sh-slice-contrib${s.contrib ? '' : ' sh-slice-contrib--none'}`}>{s.contrib ?? '—'}</span>
            </div>
          </button>
        ))}
        {slices.length === 0 && <p className="sh-nav-blurb">No slices yet. A “slice” is one month × day-type you’ve designed (e.g. August · Saturday). Design one on the Design stage, then add it here — the season case is the sum of them.</p>}
        <button type="button" className="sh-add-slice" onClick={onAddSlice} disabled={addDisabled} title={addDisabled ? 'Design a timetable first (Design stage)' : undefined}>{addLabel}</button>
      </div>

      <div className="sh-nav-foot">
        <div className="sh-nav-foot-label">Season contribution uplift</div>
        <div className={`sh-nav-foot-value${rollup.partial ? ' sh-nav-foot-value--partial' : ''}`}>{rollup.value}</div>
        <div className="sh-nav-foot-note">{rollup.note}</div>
      </div>
    </>
  )
}
